'use client';

import type { StaticImageData } from 'next/image';

import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { cn } from '@lib/utils';

import { useLiteMode } from '@/hooks/useLiteMode.hook';

export interface BackgroundVideoProps {
  poster: StaticImageData;
  narrowPoster?: StaticImageData;
  /** 폰 세로 전용 포스터. phoneRes 와 같은 크롭이어야 한다. */
  phonePoster?: StaticImageData;
  name: string;
  wideRes: string;
  narrowRes: string;
  /**
   * 폰 세로 전용 변형(`{name}-{phoneRes}`). 가로 영상을 세로 박스에 cover 하면
   * 가로의 60~74%가 화면 밖으로 버려진다.
   * phonePoster 와 함께 줘야 적용된다.
   */
  phoneRes?: string;
  breakpoint?: string;
  phoneBreakpoint?: string;
  // poster를 LCP 타겟으로 우선 로드(fetchPriority=high). hero처럼 첫 화면일 때만.
  priorityPoster?: boolean;
  className?: string;
  /** false이면 영상을 로드하지 않고 포스터만 표시한다. */
  active?: boolean;
}

export default function BackgroundVideo({
  poster,
  narrowPoster,
  phonePoster,
  name,
  wideRes,
  narrowRes,
  phoneRes,
  breakpoint = '(min-width: 990px)',
  // 현행 폰 최대 폭(430px, iPhone 16 Pro Max)을 덮는다. 가로 폰·소형 태블릿은
  // 세로 크롭이 오히려 심하게 잘리므로 441px 부터는 기존 가로 영상을 그대로 쓴다.
  phoneBreakpoint = '(max-width: 440px)',
  priorityPoster = false,
  className,
  active = true,
}: BackgroundVideoProps) {
  const isWide = useMediaQuery(breakpoint);
  const isPhone = useMediaQuery(phoneBreakpoint);
  const isLiteMode = useLiteMode();

  // 조건을 별도 boolean 으로 빼면 phoneRes 가 string 으로 좁혀지지 않아
  // res 가 string | undefined 가 된다(템플릿 리터럴이 삼켜 tsc 도 못 잡는다).
  const res =
    phoneRes && phonePoster && isPhone
      ? phoneRes
      : isWide
        ? wideRes
        : narrowRes;

  return (
    <div className="relative h-full w-full">
      {/*
        포스터는 영상을 띄울 때도 그대로 깔아 둔다. <video> 는 첫 프레임을 그리기 전까지
        투명해서(Chrome·Safari 실측) 이 이미지가 그대로 보이고, 자동재생이 차단돼도 남는다.
        poster 속성을 쓰지 않는 이유 — 속성은 미디어쿼리를 못 받아 CSS(<picture>)와
        JS(useMediaQuery)가 서로 다른 파일을 골라 포스터를 두 개 내려받는다.
        먼저 맞는 source 가 이기므로 넓은 쪽 → 폰 → 나머지(img) 순으로 둔다.
      */}
      <picture className="block h-full w-full">
        {narrowPoster ? (
          <source media={breakpoint} srcSet={poster.src} />
        ) : null}
        {phoneRes && phonePoster ? (
          <source media={phoneBreakpoint} srcSet={phonePoster.src} />
        ) : null}
        <img
          src={(narrowPoster ?? poster).src}
          alt=""
          aria-hidden="true"
          fetchPriority={priorityPoster ? 'high' : undefined}
          className={cn('h-full w-full object-cover', className)}
        />
      </picture>
      {isLiteMode || !active ? null : (
        <video
          // <source> 만 갈아끼우면 브라우저가 리소스를 다시 고르지 않는다. 폰을
          // 가로로 돌리면 세로 크롭 영상이 그대로 남아 화면이 어긋난다(실측).
          // key 로 다시 마운트해 새 티어의 소스로 처음부터 고르게 한다.
          key={res}
          className={cn(
            'absolute inset-0 h-full w-full object-cover',
            className,
          )}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src={`/videos/${name}-${res}.webm`} type="video/webm" />
          <source src={`/videos/${name}-${res}.mp4`} type="video/mp4" />
        </video>
      )}
    </div>
  );
}
